/**
 * PLA-8844: the px-length helpers for the Intelligence responsive scale.
 *
 * A leaf module on purpose. The two section trees import it directly rather than through the theme
 * barrel, so it must pull in nothing that reaches the scope components.
 *
 * The fallback inside the calc is load-bearing, not defensive. `--is-scale` is written by the
 * observer as an inline style on the content scope root and is declared NOWHERE else, so it is
 * unset whenever the flag is off and on every surface that renders outside that root - the
 * portalled BreakdownModal and GrowthPlannerSlider, and Orbit's own chart scope. An unresolvable
 * var() makes the whole calc invalid, and an invalid calc drops the ENTIRE declaration rather than
 * falling back to anything, so a floor written without the fallback would delete itself on every
 * card with the flag off. Declaring `--is-scale: 1` in the token block would fix that too, but a
 * nested block re-declaring it would then reset the scale to 1 inside an already-scaled page, which
 * is why the value lives only on the root and the default lives only here.
 */

/** The custom property the scale observer writes on the content scope root. */
export const SCALE_CSS_VAR = '--is-scale';

/** A fixed px length that follows the scale. `scaledPx(560)` is the scaled form of `560px`. */
export const scaledPx = (px: number): string => `calc(${px}px * var(${SCALE_CSS_VAR}, 1))`;

/**
 * A scaled px length that is never wider than the column holding it. For a flex-basis or a grid
 * track floor, where the scaled figure is still a floor and can overflow a column narrower than it.
 */
export const scaledFloor = (px: number): string => `min(${scaledPx(px)}, 100%)`;
